import type { GetServerSideProps } from 'next';

import { getLocaleFromParams } from '@/utils/locale';

import {
  buildUiStateFromURL,
  getIndexName,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import { getUserTokenForAlgoliaSSR } from '@/lib/algoliaUserToken';
import { commonServerSideProps } from '@/lib/commonPageStaticProps';
import { resolveContentDraftMode } from '@/lib/draftMode';
import type { LocaleMessages } from '@/lib/intl';
import { getRegionFromLocale } from '@/lib/regions';
import { CacheHeaderType } from '@/lib/revalidate';
import type { SearchViewProps } from '@/views/SearchView';
import { SearchView } from '@/views/SearchView';
import { SUPPORTED_INDICES, TypeIndexName } from '@/views/SearchView/helper';

export const getServerSideProps: GetServerSideProps<
  SearchViewProps & {
    localeMessages: LocaleMessages;
  }
> = commonServerSideProps(async (ctx) => {
  const { resolvedUrl } = ctx;
  const draftMode = resolveContentDraftMode(ctx);
  const activeTab = resolvedUrl.split('/')[3];
  const indexDataType = TypeIndexName[activeTab];

  const locale = getLocaleFromParams(ctx?.params);
  const region = getRegionFromLocale(locale);
  const productIndexName = getIndexName('PRODUCT', locale);

  if (!SUPPORTED_INDICES?.includes(indexDataType)) {
    return {
      notFound: true,
    };
  }

  // Fetch facets from Algolia for translation support
  const algoliaSsrSearchClient = createSearchClient({
    isSsr: true,
    isPreview: draftMode,
  });
  const index = algoliaSsrSearchClient.initIndex(productIndexName);

  // Fetch all facets including v2 facets for translation
  const searchResponse = await index.search('', {
    facets: ['*'],
    maxValuesPerFacet: 1000,
    hitsPerPage: 0,
  });

  // Create facet data structure for translation (not for InstantSearchSSRProvider)
  const facetDataForTranslation = {
    initialResults: {
      [productIndexName]: {
        results: [
          {
            facets: searchResponse.facets,
            renderingContent: searchResponse.renderingContent,
          },
        ],
      },
    },
  };

  // Build initial UI state with translation from English URL params to locale
  // Use activeTab as the indexId for the state since widgets are inside nested <Index> components
  const fullUiState = buildUiStateFromURL(
    resolvedUrl,
    productIndexName, // Main index name
    productIndexName,
    activeTab, // This creates state under activeTab key too
    region.locale,
    facetDataForTranslation as any,
    'EN_US', // URL values are in English, translate to current locale for UI
  );

  const searchQuery = ctx.query.q ?? '';

  // Restructure the UI state for SearchView's nested Index architecture:
  // - Root index (productIndexName) should only have 'query' for the SearchBox
  // - Nested index (activeTab like 'straps', 'watches') should have refinements
  // This prevents InstantSearch warnings about missing widgets at the root level
  const activeTabState =
    fullUiState[activeTab] || fullUiState[productIndexName] || {};
  const initialUiState = {
    // Root index: only query for SearchBox widget
    [productIndexName]: {
      query: activeTabState.query || searchQuery || '',
    },
    // Nested index: refinements and other filters for ListingFilters widgets
    [activeTab]: {
      ...activeTabState,
    },
  };

  // Generate Algolia user token server-side for consistent SSR/client rendering
  const algoliaUserToken = getUserTokenForAlgoliaSSR(ctx.req) ?? null;

  return {
    props: {
      algoliaUserToken,
      draftMode: draftMode ?? false,
      facetDataForTranslation: facetDataForTranslation as any,
      instantSearchProps: {
        indexName: productIndexName,
        initialUiState,
        query: searchQuery,
      },
      serverState: null,
    },
  };
}, CacheHeaderType.NO_CACHE);

export default SearchView;
